import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, ArrowLeft, Check, FileText, Handshake, Loader2, PackageCheck, ScanLine, Trash2, Upload } from 'lucide-react';
import { api } from '@/api/client';
import { uploadDamageProof, uploadProof, WarehouseSelect } from './RtoActionDialogs';
import type { RtoMeta } from './rtoShared';

type Action = 'RECEIVE' | 'HANDOVER' | 'RECEIVE_DAMAGED';
/** Actions that check each scan first and are then confirmed together with a proof. */
const CONFIRMED: Action[] = ['HANDOVER', 'RECEIVE_DAMAGED'];
/** Actions done at a GT warehouse, which need it chosen. */
const AT_WAREHOUSE: Action[] = ['RECEIVE', 'RECEIVE_DAMAGED'];
type RowStatus = 'QUEUED' | 'SUCCESS' | 'READY' | 'ALREADY_PROCESSED' | 'INVALID' | 'DUPLICATE' | 'FAILED';
interface Row {
  key: number; input: string; status: RowStatus; message: string; at: Date;
  /** The condition this parcel arrived in. Chosen per row, sent with that AWB. */
  condition: string;
  awb?: string | null; orderType?: string; merchant?: string | null; currentStatus?: string | null;
}

const BATCH = 50;
const TONE: Record<RowStatus, string> = {
  QUEUED: 'bg-slate-100 text-slate-600', SUCCESS: 'bg-green-50 text-green-700', READY: 'bg-sky-50 text-sky-700',
  ALREADY_PROCESSED: 'bg-amber-50 text-amber-700', INVALID: 'bg-red-50 text-red-700', DUPLICATE: 'bg-slate-100 text-slate-600',
  FAILED: 'bg-red-50 text-red-700',
};
const LABEL: Record<RowStatus, string> = {
  QUEUED: 'Checking…', SUCCESS: 'Success', READY: 'Ready', ALREADY_PROCESSED: 'Already Processed', INVALID: 'Invalid',
  DUPLICATE: 'Duplicate', FAILED: 'Failed',
};

/**
 * What the warehouse found when the parcel arrived, chosen per scanned row.
 *
 * "RTO Received" leads because it is the ordinary case and becomes the default — the exceptions are
 * what need saying, and a default of nothing means the common case gets a blank remark.
 */
const RTO_CONDITIONS = [
  'RTO Received',
  'Partial RTO Received',
  'Damage Received',
  'Wrong Product Received',
] as const;

/** A short tone per scan, so the operator can keep eyes on the parcels instead of the screen. */
function beep(ok: boolean) {
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = ok ? 880 : 220;
    g.gain.value = 0.05;
    o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + (ok ? 0.08 : 0.25));
    setTimeout(() => ctx.close?.(), 400);
  } catch { /* sound is a nicety, never a failure */ }
}

export function RtoScanPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: meta, isLoading } = useQuery<RtoMeta>({ queryKey: ['rto-meta'], queryFn: async () => (await api.get('/orders/rto/meta')).data });

  const [action, setAction] = useState<Action | null>(null);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [remarks, setRemarks] = useState('');
  const [input, setInput] = useState('');
  // The condition applied to each newly added row; each row can then be changed on its own.
  const [condition, setCondition] = useState<string>(RTO_CONDITIONS[0]);
  /*
   * Hold scans until Submit, rather than firing 500 ms after each one.
   *
   * Necessary now that a row carries its own condition: an auto-send gives nobody time to say box 3
   * came in damaged. A gun user who wants the old rhythm turns auto-submit back on.
   */
  const [autoSubmit, setAutoSubmit] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [inFlight, setInFlight] = useState(false);
  const [proofRef, setProofRef] = useState('');
  const [proofName, setProofName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const keyRef = useRef(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { if (meta?.defaultBranchId && branchId === '') setBranchId(meta.defaultBranchId); }, [meta, branchId]);
  useEffect(() => { if (action) inputRef.current?.focus(); }, [action]);

  const actionMeta = meta?.scanActions.find((a) => a.action === action);

  /**
   * Add everything in the box, not just one value.
   *
   * A gun sends one code and an Enter, which lands here as a single value and behaves exactly as
   * before. A pasted column of 40 LRs lands here as 40 — split on commas, spaces, semicolons, pipes
   * and line breaks, because a sheet column pasted into a textarea arrives with line breaks and a
   * copied list arrives with commas.
   */
  const addScan = () => {
    const parts = input.split(/[\s,;|]+/).map((x) => x.trim()).filter(Boolean);
    setInput('');
    if (!parts.length || !action) return;
    const added: Row[] = [];
    const seen = new Set(rows.map((r) => r.input.toUpperCase()));
    let anyDup = false;
    for (const v of parts) {
      const key = v.toUpperCase();
      // Duplicates WITHIN the paste are caught too, not just against what is already listed.
      const dup = seen.has(key);
      seen.add(key);
      if (dup) anyDup = true;
      keyRef.current += 1;
      added.push(dup
        ? { key: keyRef.current, input: v, condition, status: 'DUPLICATE', message: 'Already scanned in this session.', at: new Date() }
        : { key: keyRef.current, input: v, condition, status: 'QUEUED', message: '', at: new Date() });
    }
    if (anyDup) beep(false);
    setRows((rs) => [...added.reverse(), ...rs]);
  };

  // Scans are sent in batches as they arrive, not one request per beep; each AWB still gets its own
  // verdict, and one bad AWB never holds up the others.
  const flush = useCallback(async () => {
    if (!action || inFlight || confirming) return;
    const queued = rows.filter((r) => r.status === 'QUEUED').slice(-BATCH);
    if (!queued.length) return;
    if (AT_WAREHOUSE.includes(action) && !branchId) return;
    setInFlight(true);
    try {
      const { data } = await api.post('/orders/rto/scan/bulk', {
        action, awbs: queued.map((r) => r.input), branchId: branchId || undefined,
        // Each row carries the condition chosen against it; the session remark is the fallback.
        perAwb: queued.map((r) => ({ awb: r.input, condition: r.condition })),
        remarks: remarks.trim() || undefined, validateOnly: CONFIRMED.includes(action),
      }, { timeout: 120_000 });
      const byInput = new Map<string, any>();
      for (const r of data.results ?? []) byInput.set(String(r.input).toUpperCase(), r);
      let anyBad = false;
      setRows((rs) => rs.map((row) => {
        if (row.status !== 'QUEUED') return row;
        const r = byInput.get(row.input.toUpperCase());
        if (!r) return row;
        if (!['SUCCESS', 'READY'].includes(r.status)) anyBad = true;
        return { ...row, status: r.status, message: r.message, awb: r.awb, orderType: r.orderType, merchant: r.merchant, currentStatus: r.currentStatus };
      }));
      beep(!anyBad);
      if (action === 'RECEIVE') { qc.invalidateQueries({ queryKey: ['rto-list'] }); qc.invalidateQueries({ queryKey: ['rto-summary'] }); }
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? 'Could not reach the server';
      setRows((rs) => rs.map((row) => (queued.some((q) => q.key === row.key) ? { ...row, status: 'FAILED', message: msg } : row)));
      beep(false);
    } finally {
      setInFlight(false);
    }
  }, [action, inFlight, confirming, rows, branchId, remarks, qc]);

  useEffect(() => {
    if (!autoSubmit) return;
    const t = setTimeout(() => { flush(); }, 500);
    return () => clearTimeout(t);
  }, [rows, flush, autoSubmit]);

  const ready = rows.filter((r) => r.status === 'READY');

  async function pickProof(file?: File | null) {
    if (!file) return;
    const type = ready.length && ready.every((r) => r.orderType === 'B2B') ? 'B2B' : 'B2C';
    setUploading(true);
    try { setProofRef(action === 'RECEIVE_DAMAGED' ? await uploadDamageProof(file) : await uploadProof(file, type)); setProofName(file.name); }
    catch (e: any) { toast.error(e?.response?.data?.message ?? e?.message ?? 'Upload failed'); }
    finally { setUploading(false); }
  }

  async function confirmHandover() {
    if (!ready.length || !proofRef) return;
    const damaged = action === 'RECEIVE_DAMAGED';
    if (damaged && !remarks.trim()) { toast.error('Remarks are required — describe the damage.'); return; }
    setConfirming(true);
    try {
      const { data } = await api.post('/orders/rto/scan/bulk', {
        action: damaged ? 'RECEIVE_DAMAGED' : 'HANDOVER', awbs: ready.map((r) => r.input), proofRef, remarks: remarks.trim() || undefined,
        ...(damaged ? { branchId: branchId || undefined, perAwb: ready.map((r) => ({ awb: r.input, condition: r.condition })) } : {}),
      }, { timeout: 180_000 });
      const byInput = new Map<string, any>();
      for (const r of data.results ?? []) byInput.set(String(r.input).toUpperCase(), r);
      setRows((rs) => rs.map((row) => {
        const r = row.status === 'READY' ? byInput.get(row.input.toUpperCase()) : null;
        return r ? { ...row, status: r.status, message: r.message, currentStatus: r.currentStatus } : row;
      }));
      toast.success(damaged
        ? `${data.counters?.successful ?? 0} shipment(s) recorded as received damaged — a private ticket was raised for the CS team`
        : `${data.counters?.successful ?? 0} shipment(s) marked delivered to the merchant`);
      setProofRef(''); setProofName('');
      qc.invalidateQueries({ queryKey: ['rto-list'] }); qc.invalidateQueries({ queryKey: ['rto-summary'] });
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Could not confirm the delivery');
    } finally { setConfirming(false); inputRef.current?.focus(); }
  }

  function changeAction(a: Action) {
    if (rows.length && !window.confirm('Changing the status clears this scan session. Continue?')) return;
    setRows([]); setProofRef(''); setProofName(''); setAction(a);
    setCondition(a === 'RECEIVE_DAMAGED' ? 'Damage Received' : RTO_CONDITIONS[0]);
  }

  const count = (s: RowStatus) => rows.filter((r) => r.status === s).length;
  const counters: Array<[string, number, string]> = [
    ['Total Scanned', rows.length, 'text-slate-900'],
    ['Successful', count('SUCCESS'), 'text-green-700'],
    ...(action && CONFIRMED.includes(action) ? [['Ready', count('READY'), 'text-sky-700'] as [string, number, string]] : []),
    ['Failed', count('FAILED'), 'text-red-700'],
    ['Duplicate', count('DUPLICATE'), 'text-slate-600'],
    ['Invalid', count('INVALID'), 'text-red-700'],
    ['Already Processed', count('ALREADY_PROCESSED'), 'text-amber-700'],
  ];

  if (isLoading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  if (meta && (meta.isMerchant || (!meta.permissions.receive && !meta.permissions.handover))) {
    return <div className="p-6 text-sm text-slate-600">RTO scanning needs the RTO Orders "Receive at Warehouse" or "Deliver to Merchant" permission.</div>;
  }

  return (
    <div className="p-6 max-w-[1300px] mx-auto">
      <button onClick={() => navigate('/rto-orders')} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2"><ArrowLeft size={14} /> RTO Orders</button>
      <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mb-4"><ScanLine size={22} /> Scan RTO</h1>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
        <div className="text-xs font-semibold uppercase text-slate-500 mb-2">1. Select status</div>
        <div className="grid md:grid-cols-3 gap-3">
          {(meta?.scanActions ?? []).map((a) => {
            const allowed = a.action === 'HANDOVER' ? meta?.permissions.handover : meta?.permissions.receive;
            const selected = action === a.action;
            return (
              <button key={a.action} disabled={!allowed} onClick={() => changeAction(a.action)}
                className={`text-left border rounded-xl p-3 flex items-start gap-3 ${selected ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'border-slate-200 hover:bg-slate-50'} disabled:opacity-40`}>
                {a.action === 'RECEIVE' ? <PackageCheck className="text-indigo-600 mt-0.5" size={20} />
                  : a.action === 'RECEIVE_DAMAGED' ? <AlertTriangle className="text-rose-600 mt-0.5" size={20} />
                  : <Handshake className="text-emerald-600 mt-0.5" size={20} />}
                <div>
                  <div className="font-medium text-slate-900">{a.name}</div>
                  <div className="text-xs text-slate-500">
                    {a.action === 'RECEIVE' ? 'Parcel is physically on our dock.'
                      : a.action === 'RECEIVE_DAMAGED' ? 'On our dock, but damaged. Photo or video and remarks are required; a private ticket goes to the CS team.'
                      : 'Parcel handed back to the merchant. Proof is required.'}
                    {!allowed ? ' (no permission)' : ''}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        {action && (
          <div className="grid md:grid-cols-2 gap-3 mt-3">
            {AT_WAREHOUSE.includes(action) && (
              <div>
                <label className="text-xs text-slate-500 mb-1 block">GT warehouse *</label>
                <WarehouseSelect meta={meta} value={branchId} onChange={setBranchId} />
              </div>
            )}
            <div>
              <label className="text-xs text-slate-500 mb-1 block">
                {action === 'RECEIVE_DAMAGED' ? <>Remarks — describe the damage <span className="text-rose-600">*</span></> : 'Remarks for this session (optional)'}
              </label>
              <input value={remarks} onChange={(e) => setRemarks(e.target.value)} maxLength={400}
                className={`input w-full ${action === 'RECEIVE_DAMAGED' && !remarks.trim() ? 'border-rose-300' : ''}`} />
            </div>
          </div>
        )}
      </div>

      {action && (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
            <div className="text-xs font-semibold uppercase text-slate-500 mb-2">2. Scan AWB / LR / order barcode</div>
            <form onSubmit={(e) => { e.preventDefault(); addScan(); }} className="space-y-2">
              {/* A textarea so a column of numbers can be pasted straight in. Enter still submits,
                  which keeps a barcode gun working exactly as before; Shift+Enter makes a new line. */}
              <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} autoFocus rows={3}
                disabled={AT_WAREHOUSE.includes(action) && !branchId}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addScan(); } }}
                placeholder={AT_WAREHOUSE.includes(action) && !branchId
                  ? 'Select the GT warehouse first'
                  : 'Scan one at a time, or paste many AWB / LR numbers — commas, spaces or new lines'}
                className="input w-full font-mono text-base resize-y" />
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <label className="text-[11px] text-slate-500 block mb-0.5">Condition for what you add next</label>
                  <select value={condition} onChange={(e) => setCondition(e.target.value)} className="input py-2 text-sm">
                    {RTO_CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <button type="submit" className="px-4 py-2 bg-primary text-white rounded-lg text-sm">Add</button>
                <button type="button" onClick={() => flush()} disabled={inFlight || !rows.some((r) => r.status === 'QUEUED')}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                  {inFlight ? 'Submitting…' : `Submit ${rows.filter((r) => r.status === 'QUEUED').length || ''}`}
                </button>
                <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer ml-auto"
                  title="Send each scan the moment it is added, without a review step">
                  <input type="checkbox" checked={autoSubmit} onChange={(e) => setAutoSubmit(e.target.checked)} className="rounded" />
                  Submit automatically
                </label>
              </div>
              {!autoSubmit && rows.some((r) => r.status === 'QUEUED') && (
                <p className="text-[11px] text-amber-700">
                  {rows.filter((r) => r.status === 'QUEUED').length} waiting — set each row's condition below, then press Submit.
                </p>
              )}
            </form>
            <div className="grid grid-cols-3 md:grid-cols-7 gap-2 mt-3">
              {counters.map(([label, value, tone]) => (
                <div key={label} className="bg-slate-50 rounded-lg px-3 py-2">
                  <div className={`text-lg font-bold ${tone}`}>{value}</div>
                  <div className="text-[11px] text-slate-500">{label}</div>
                </div>
              ))}
            </div>
          </div>

          {action && CONFIRMED.includes(action) && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
              <div className="text-xs font-semibold uppercase text-slate-500 mb-2">
                {action === 'RECEIVE_DAMAGED' ? '3. Upload a photo or video of the damage and confirm' : '3. Upload proof and confirm'}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 border border-dashed border-slate-300 rounded-lg px-3 py-2 cursor-pointer hover:bg-slate-50 text-sm">
                  {uploading ? <Loader2 size={15} className="animate-spin" /> : proofRef ? <Check size={15} className="text-emerald-600" /> : <Upload size={15} />}
                  <span className={proofRef ? 'text-emerald-700' : 'text-slate-600'}>
                    {proofRef ? proofName : action === 'RECEIVE_DAMAGED'
                      ? 'Upload photo or video of the damage (JPG, PNG, WEBP, MP4, MOV, WEBM — 20 MB) *'
                      : 'Upload POD / RTO delivery proof (PDF, JPG, PNG, 5 MB)'}
                  </span>
                  <input type="file" className="hidden" disabled={!ready.length} onChange={(e) => pickProof(e.target.files?.[0])}
                    accept={action === 'RECEIVE_DAMAGED' ? 'image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm' : 'application/pdf,image/jpeg,image/png'} />
                </label>
                <div className="text-sm text-slate-700">Selected orders: <b>{ready.length}</b></div>
                <button onClick={confirmHandover}
                  disabled={!ready.length || !proofRef || confirming || inFlight || (action === 'RECEIVE_DAMAGED' && !remarks.trim())}
                  className={`ml-auto px-4 py-2 text-white rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50 ${action === 'RECEIVE_DAMAGED' ? 'bg-rose-600' : 'bg-emerald-600'}`}>
                  {confirming ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                  {action === 'RECEIVE_DAMAGED' ? 'Confirm Received (Damaged)' : 'Confirm RTO Delivery'}
                </button>
              </div>
              {!!ready.length && (
                <div className="text-xs text-slate-500 mt-2">
                  This proof will be linked to: <span className="font-mono text-slate-700">{ready.map((r) => r.awb ?? r.input).join(', ')}</span>
                </div>
              )}
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="flex items-center px-4 py-2 border-b border-slate-100">
              <div className="text-sm font-medium text-slate-700">{actionMeta?.name} {String.fromCharCode(183)} session</div>
              {inFlight && <Loader2 size={14} className="animate-spin ml-2 text-slate-400" />}
              {!!rows.length && (
                <button onClick={() => { if (window.confirm('Clear this scan list? Anything already processed stays processed.')) setRows([]); }}
                  className="ml-auto text-xs text-slate-500 hover:text-red-600 flex items-center gap-1"><Trash2 size={13} /> Clear list</button>
              )}
            </div>
            <div className="overflow-x-auto max-h-[520px]">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">Scanned</th>
                    <th className="text-left px-3 py-2">AWB / LR</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Merchant</th>
                    <th className="text-left px-3 py-2">Condition</th>
                    <th className="text-left px-3 py-2">Result</th>
                    <th className="text-left px-3 py-2">Details</th>
                    <th className="text-left px-3 py-2">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {!rows.length && <tr><td colSpan={8} className="py-10 text-center text-slate-400">Scans appear here.</td></tr>}
                  {rows.map((r) => (
                    <tr key={r.key} className="border-t border-slate-100">
                      <td className="px-3 py-1.5 font-mono text-xs">{r.input}</td>
                      <td className="px-3 py-1.5 font-mono text-xs">{r.awb ?? '—'}</td>
                      <td className="px-3 py-1.5 text-xs">{r.orderType ?? '—'}</td>
                      <td className="px-3 py-1.5 text-xs max-w-[180px] truncate" title={r.merchant ?? ''}>{r.merchant ?? '—'}</td>
                      <td className="px-3 py-1.5">
                        {/* Editable while the row is still waiting; once it has been sent the
                            condition is part of what was recorded, so it is shown, not offered. */}
                        {r.status === 'QUEUED' ? (
                          <select value={r.condition}
                            onChange={(e) => setRows((rs) => rs.map((x) => (x.key === r.key ? { ...x, condition: e.target.value } : x)))}
                            className={`input py-0.5 text-[11px] ${r.condition !== RTO_CONDITIONS[0] ? 'border-amber-400 text-amber-800' : ''}`}>
                            {RTO_CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        ) : (
                          <span className={`text-[11px] ${r.condition !== RTO_CONDITIONS[0] ? 'text-amber-700 font-medium' : 'text-slate-500'}`}>{r.condition}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${TONE[r.status]}`}>{LABEL[r.status]}</span></td>
                      <td className="px-3 py-1.5 text-xs text-slate-700">{r.message}{r.currentStatus && r.status !== 'SUCCESS' ? ` (now ${r.currentStatus})` : ''}</td>
                      <td className="px-3 py-1.5 text-xs text-slate-500 whitespace-nowrap">{r.at.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
